import {
  Card,
  CardContent,
  DataTable,
  Flex,
  Heading,
  Select,
  Text,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';
import type { ColumnDef, RowData, StockFeatures } from '@tanstack/react-table';

type Props<TElement extends RowData> = {
  /** Every catalogued element, before filtering — drives the empty-state copy. */
  taxonomyElements: TElement[];
  /** Elements after the search + type filters are applied. */
  filteredElements: TElement[];
  /** Generic over the row type so the caller's ColumnDef isn't widened to unknown. */
  taxonomyColumns: ColumnDef<StockFeatures, TElement, unknown>[];
  taxonomySearch: string;
  onSearchChange: (value: string) => void;
  taxonomyTypeFilter: string;
  onTypeFilterChange: (value: string) => void;
  typeOptions: SelectOption[];
};

/**
 * Interactive elements catalogued from the latest crawl snapshot.
 *
 * Extracted out of PageDetail.tsx, which had reached the 1,000-line refactor
 * threshold in docs/project/code-quality.md — that rule forbids adding logic to
 * such a file, so this moved out as the page-skill panel moved in.
 */
export function ElementTaxonomyCard<TElement extends RowData>({
  taxonomyElements,
  filteredElements,
  taxonomyColumns,
  taxonomySearch,
  onSearchChange,
  taxonomyTypeFilter,
  onTypeFilterChange,
  typeOptions,
}: Props<TElement>) {
  return (
    <Card variant="default" padding="lg">
      <CardContent className="space-y-4">
        <Flex align="center" justify="between" wrap gap="md">
          <div className="space-y-1">
            <Heading size="sm">Element taxonomy</Heading>
            <Text size="sm" className="text-muted-foreground">
              Interactive elements catalogued from the latest crawl snapshot (excluding global nav).
            </Text>
          </div>
          <Flex gap="sm" wrap className="w-full md:w-auto">
            <TextField
              aria-label="Search elements"
              placeholder="Search elements"
              value={taxonomySearch}
              onChange={(value) => onSearchChange(typeof value === 'string' ? value : '')}
              className="w-full md:w-64"
              variant="filled"
            />
            <Select
              aria-label="Filter by element type"
              selectedKey={taxonomyTypeFilter}
              onSelectionChange={(key) => onTypeFilterChange(String(key))}
              items={typeOptions}
              placeholder="Filter by type"
              className="w-full md:w-48"
            />
          </Flex>
        </Flex>

        {filteredElements.length > 0 ? (
          <DataTable
            columns={taxonomyColumns}
            data={filteredElements}
            aria-label="Element taxonomy"
          />
        ) : (
          <div className="border border-dashed rounded-lg p-6 text-center">
            <Text className="text-muted-foreground">
              {taxonomyElements.length > 0
                ? 'No elements match your filters.'
                : 'No element taxonomy captured yet for this page.'}
            </Text>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
